import { AIService } from './AIService';
import { 
  PlaylistData, 
  PlaylistModificationOptions, 
  ChatResponse 
} from './types';
import { log } from './utils';

export class PlaylistModificationService {
  constructor(private aiService: AIService) {}
  
  // Modify an existing playlist based on instructions
  public async modifyPlaylist(
    playlist: PlaylistData,
    instructions: string,
    options?: PlaylistModificationOptions
  ): Promise<ChatResponse> {
    let prompt = `I have an existing playlist that I'd like you to modify based on these instructions: "${instructions}".`;
    
    prompt += `\n\nHere's the current playlist:`;
    prompt += `\nName: ${playlist.playlist_name}`;
    prompt += `\nDescription: ${playlist.description}`;
    prompt += `\nTracks:`;
    
    playlist.tracks.forEach((track, index) => {
      prompt += `\n${index + 1}. "${track.track}" by ${track.artist}`;
    });
    
    if (options?.addTracks) {
      prompt += `\n\nPlease add approximately ${options.addTracks} new tracks that fit the playlist and the modification instructions.`;
    }
    
    if (options?.removeTracks) {
      prompt += `\n\nPlease remove approximately ${options.removeTracks} tracks that least fit the modification instructions.`;
    }
    
    if (options?.keepPercentage) {
      prompt += `\n\nKeep approximately ${options.keepPercentage}% of the original tracks.`;
    }
    
    prompt += `\n\nPlease return the modified playlist as a JSON object with the following structure:
    {
      "playlist_name": "Updated name if appropriate",
      "description": "Updated description that reflects the changes",
      "tracks": [
        {"artist": "Artist name", "track": "Track name", "reason": "Reason for inclusion or why this track was kept/added"}
      ]
    }`;
    
    log('Modifying playlist with prompt:', prompt);
    const response = await this.aiService.sendMessage(prompt);
    return response;
  }
  
  // Transform a playlist based on instructions
  public async transformPlaylist(
    playlist: PlaylistData,
    transformation: string
  ): Promise<ChatResponse> {
    let prompt = `I have a playlist that I'd like to transform according to this instruction: "${transformation}".`;
    prompt += `\n\nThe transformation should maintain the essence of the original playlist but adapt it according to the instructions.`;
    
    prompt += `\n\nOriginal playlist:`;
    prompt += `\nName: ${playlist.playlist_name}`;
    prompt += `\nDescription: ${playlist.description}`;
    prompt += `\nTracks:`;
    
    playlist.tracks.forEach((track, index) => {
      prompt += `\n${index + 1}. "${track.track}" by ${track.artist}`;
    });
    
    prompt += `\n\nPlease create a transformed version of this playlist that:`;
    prompt += `\n- Follows the transformation instruction`;
    prompt += `\n- Maintains the core theme or concept`;
    prompt += `\n- Replaces tracks as needed to achieve the transformation`;
    prompt += `\n- Creates a cohesive listening experience`;
    
    prompt += `\n\nPlease format the transformed playlist as a JSON object with the following structure:
    {
      "playlist_name": "A name that reflects the transformed playlist",
      "description": "A description that explains how the playlist was transformed",
      "tracks": [
        {"artist": "Artist name", "track": "Track name", "reason": "Why this track fits the transformation"}
      ]
    }`;
    
    log('Transforming playlist with prompt:', prompt);
    const response = await this.aiService.sendMessage(prompt);
    return response;
  }
  
  // Merge two playlists with AI assistance
  public async mergePlaylists(
    playlist1: PlaylistData,
    playlist2: PlaylistData,
    instructions?: string
  ): Promise<ChatResponse> {
    let prompt = `I have two playlists that I'd like to merge into a single cohesive playlist.`;
    
    if (instructions) {
      prompt += ` Please follow these specific instructions: "${instructions}".`;
    }
    
    prompt += `\n\nPlaylist 1:`;
    prompt += `\nName: ${playlist1.playlist_name}`;
    prompt += `\nDescription: ${playlist1.description}`;
    prompt += `\nTracks:`;
    
    playlist1.tracks.forEach((track, index) => {
      prompt += `\n${index + 1}. "${track.track}" by ${track.artist}`;
    });
    
    prompt += `\n\nPlaylist 2:`;
    prompt += `\nName: ${playlist2.playlist_name}`;
    prompt += `\nDescription: ${playlist2.description}`;
    prompt += `\nTracks:`;
    
    playlist2.tracks.forEach((track, index) => {
      prompt += `\n${index + 1}. "${track.track}" by ${track.artist}`;
    });
    
    prompt += `\n\nPlease create a merged playlist that:`;
    prompt += `\n- Combines elements from both playlists`;
    prompt += `\n- Creates a cohesive listening experience`;
    prompt += `\n- Avoids duplicate tracks or artists unless they're essential`;
    prompt += `\n- Includes approximately 15-20 tracks total`;
    prompt += `\n- Has a logical flow and sequence`;
    
    prompt += `\n\nPlease format the merged playlist as a JSON object with the following structure:
    {
      "playlist_name": "A name that reflects the merged content",
      "description": "A description that explains the merged playlist concept",
      "tracks": [
        {"artist": "Artist name", "track": "Track name", "reason": "Why this track was included in the merged playlist"}
      ]
    }`;
    
    log('Merging playlists with prompt:', prompt);
    const response = await this.aiService.sendMessage(prompt);
    return response;
  }
}
